import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { Apis } from "../../../lib/Apis";
import "../login-page/style.css";

const VerifyOtpPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const isPasswordReset = searchParams.get("isPasswordReset") === "true";

  const [otp, setOtp] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const inputRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ];

  useEffect(() => {
    if (!email) {
      navigate("/login");
    }
  }, [email, navigate]);

  const handleChange = (index, value) => {
    const sanitizedValue = value.replace(/[^0-9]/g, "");

    if (sanitizedValue.length <= 1) {
      const updatedOtp = [...otp];
      updatedOtp[index] = sanitizedValue;
      setOtp(updatedOtp);

      if (sanitizedValue && index < otp.length - 1) {
        inputRefs[index + 1]?.current?.focus();
      }
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs[index - 1]?.current?.focus();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const otpValue = otp.join("");

    if (otpValue.length !== 4) {
      Swal.fire({
        icon: "warning",
        title: "Invalid OTP",
        text: "Please enter the 4-digit verification code.",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await Apis.verifyOtp({
        email,
        otp: otpValue,
      });

      if (response?.status === 200 || response?.data?.success) {
        if (response?.data?.token) {
          localStorage.setItem("token", response.data.token);
        }

        Swal.fire({
          icon: "success",
          title: "OTP verified",
          text: response?.data?.message || "Your OTP has been verified.",
        });

        navigate(isPasswordReset ? "/reset-password" : "/login");
      } else {
        Swal.fire({
          icon: "error",
          title: "Verification failed",
          text:
            response?.data?.message ||
            "The code you entered is invalid. Please try again.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Verification failed",
        text:
          error?.response?.data?.message ||
          "Unable to verify OTP right now.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!email) return;

    setResendLoading(true);

    try {
      const response = await Apis.forgotPassword({ email });
      Swal.fire({
        icon: "success",
        title: "OTP resent",
        text:
          response?.data?.message ||
          "A new OTP has been sent to your email.",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Resend failed",
        text:
          error?.response?.data?.message ||
          "Unable to resend OTP right now.",
      });
    } finally {
      setResendLoading(false);
    }
  };

  if (!email) return null;

  return (
    <div className="auth-main-layout">
      <div className="auth-glass-box">
        <form onSubmit={handleSubmit}>
          <h1>Verify OTP</h1>
          <p className="auth-centered-text" style={{ marginTop: 10 }}>
            Enter the 4-digit code sent to {email}
          </p>

          <div
            className="auth-field-wrapper"
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              marginTop: 24,
            }}
          >
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={inputRefs[index]}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                style={{
                  width: 52,
                  height: 52,
                  textAlign: "center",
                  padding: 0,
                  fontSize: 22,
                }}
              />
            ))}
          </div>

          <button
            type="submit"
            className="auth-login-action"
            style={{ marginTop: 20 }}
            disabled={loading}
          >
            {loading ? "Verifying..." : "Verify OTP"}
          </button>

          <button
            type="button"
            className="auth-login-action"
            style={{
              marginTop: 12,
              background: "transparent",
              color: "#fff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
            }}
            onClick={handleResendOtp}
            disabled={resendLoading}
          >
            {resendLoading ? "Resending..." : "Resend OTP"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default VerifyOtpPage;
